import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import type {
  NotificationChannel,
  NotificationEventType,
} from './notification-preferences';
import { getEnabledChannels } from './notification-preferences';

/**
 * Store de notificações IN-APP — **backend-first (commit 6, 2026-05-18)**.
 *
 * Antes: store em memória + `dispatchNotification` síncrono.
 * Agora: lê `GET /api/v1/notifications` (polled 30s) + mutations PUT.
 *
 * Contrato dos hooks IDÊNTICO a antes:
 *   - useNotifications(userId): Notification[]
 *   - useUnreadCount(userId): number
 *   - useNotificationActions(userId): { markRead, markAllRead }
 *
 * Compat: `dispatchNotification(userId, type, payload)` virou async wrapper
 * sobre `POST /api/v1/notifications` — callers existentes (tests) seguem
 * funcionando como fire-and-forget (não-await retorna Promise).
 *
 * Regra 8.1 (in-app sempre on): backend cria row mesmo se prefs zeradas
 * pra esse tipo. Helper `getEnabledChannels` resolve canais externos
 * (email/whatsapp/push) — chamado pelo BACKEND quando ele implementar
 * envio real (R7+). R0 só in-app é entregue de verdade.
 */

export type NotificationPayload = Record<string, unknown>;

export interface Notification {
  id: string;
  userId: string;
  type: NotificationEventType;
  payload: NotificationPayload;
  lida: boolean;
  criadoEmIso: string;
  /**
   * BUG-V2-042 / Migration 046: marca quando o side effect (confete,
   * push, etc) já foi disparado pra essa notif. Distinto de `lida`
   * (intenção explícita do user). Opcional: backend pré-046 retorna
   * null/undefined → trata como "ainda não dispatched".
   */
  dispatchedAtIso?: string | null;
}

interface BackendNotification {
  id: string;
  userId: string;
  type: string;
  payload: NotificationPayload;
  lida: boolean;
  criadoEmIso: string;
  dispatchedAtIso?: string | null;
}

const POLL_INTERVAL_MS = 30_000;
const KEYS = {
  list: (userId: string) => ['v1', 'notifications', userId] as const,
};

function shape(row: BackendNotification): Notification {
  return {
    id: row.id,
    userId: row.userId,
    type: row.type as NotificationEventType,
    payload: row.payload ?? {},
    lida: !!row.lida,
    criadoEmIso: row.criadoEmIso,
    dispatchedAtIso: row.dispatchedAtIso ?? null,
  };
}

// ─── Hooks reativos ────────────────────────────────────────────────────

export function useNotifications(userId: string): Notification[] {
  const { data } = useQuery({
    queryKey: KEYS.list(userId),
    queryFn: async () => {
      const rows = await api.get<BackendNotification[]>('/api/v1/notifications');
      return rows.map(shape);
    },
    enabled: hasToken() && !!userId && userId !== 'anonymous',
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false, // pausa quando aba não tem foco
    staleTime: 10_000,
  });
  return data ?? [];
}

// ─── Histórico paginado (FEAT-V2-028) ──────────────────────────────────

export interface NotificationsHistoryFilters {
  type?: string | null;
  /** true=só lidas, false=só não-lidas, null/undefined=todas. */
  lida?: boolean | null;
  /** ISO date `YYYY-MM-DD` (inclusivo). */
  from?: string | null;
  to?: string | null;
}

export interface NotificationsHistoryPage {
  items: Notification[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface BackendNotificationsPage {
  items: BackendNotification[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/**
 * Histórico paginado pra `/perfil/notificacoes`. Diferente de
 * `useNotifications` (dropdown do sino), aqui passa `page` no query e
 * recebe envelope `{items, total, hasMore}`. Sem polling — pagina é
 * "snapshot" do momento (user click "Próxima").
 */
export function useNotificationsHistory(
  userId: string,
  page: number,
  filters: NotificationsHistoryFilters = {},
  limit = 50,
) {
  return useQuery({
    queryKey: ['v1', 'notifications', 'history', userId, page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filters.type) params.set('type', filters.type);
      if (filters.lida === true) params.set('lida', 'true');
      else if (filters.lida === false) params.set('lida', 'false');
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const data = await api.get<BackendNotificationsPage>(
        `/api/v1/notifications?${params.toString()}`,
      );
      return {
        ...data,
        items: data.items.map(shape),
      } as NotificationsHistoryPage;
    },
    enabled: hasToken() && !!userId && userId !== 'anonymous',
    staleTime: 30_000,
  });
}

export function useUnreadCount(userId: string): number {
  const list = useNotifications(userId);
  return useMemo(() => list.filter((n) => !n.lida).length, [list]);
}

export function useNotificationActions(userId: string) {
  const qc = useQueryClient();

  const markRead = useCallback(
    async (id: string) => {
      // Optimistic: marca lida no cache imediato
      qc.setQueryData<Notification[]>(KEYS.list(userId), (prev) =>
        prev ? prev.map((n) => (n.id === id ? { ...n, lida: true } : n)) : prev
      );
      try {
        await api.put(`/api/v1/notifications/${id}/read`);
      } catch {
        // Rollback: refetch garante consistência
        await qc.invalidateQueries({ queryKey: KEYS.list(userId) });
      }
    },
    [qc, userId]
  );

  const markAllRead = useCallback(async () => {
    qc.setQueryData<Notification[]>(KEYS.list(userId), (prev) =>
      prev ? prev.map((n) => ({ ...n, lida: true })) : prev
    );
    try {
      await api.put('/api/v1/notifications/read-all');
    } catch {
      await qc.invalidateQueries({ queryKey: KEYS.list(userId) });
    }
  }, [qc, userId]);

  /**
   * BUG-V2-042: marca notificação como "side effect já disparado" (confete,
   * toast, push). Distinto de `markRead` — uma celebração pode ficar
   * dispatched MAS não-lida (user viu confete e fechou app sem clicar).
   *
   * Optimistic: atualiza cache imediato com timestamp ISO local pra que
   * filtros downstream (`!n.dispatchedAtIso`) excluam imediatamente.
   * Rollback via invalidate se POST falhar.
   *
   * Fire-and-forget: caller (CelebrationListener) não precisa await — o
   * UI já reagiu via cache otimista. Erro de rede só causa re-fire no
   * próximo boot, aceitável.
   */
  const markDispatched = useCallback(
    async (id: string) => {
      const nowIso = new Date().toISOString();
      qc.setQueryData<Notification[]>(KEYS.list(userId), (prev) =>
        prev
          ? prev.map((n) => (n.id === id ? { ...n, dispatchedAtIso: nowIso } : n))
          : prev,
      );
      try {
        await api.post(`/api/v1/notifications/${id}/dispatched`);
      } catch {
        await qc.invalidateQueries({ queryKey: KEYS.list(userId) });
      }
    },
    [qc, userId],
  );

  return { markRead, markAllRead, markDispatched };
}

// ─── Compat wrapper (callers antigos) ──────────────────────────────────

/**
 * Wrapper async sobre `POST /api/v1/notifications`. Mantido por compat —
 * callers existentes (tests + alguns fluxos legacy) chamam sem await.
 *
 * Backend cria row + responde com a notificação criada. Frontend pode
 * usar `useNotifications` polling pra ver o resultado.
 *
 * Em fluxos novos (automations), backend cria row diretamente via
 * `executeAction()` no engine — frontend NÃO chama dispatchNotification.
 */
export async function dispatchNotification(
  userId: string,
  type: NotificationEventType,
  payload: NotificationPayload = {}
): Promise<Notification> {
  // getEnabledChannels é chamado defensivamente — backend ignora canais
  // por enquanto, mas o lookup mantém o contrato vivo pra R7.
  const channels: NotificationChannel[] = getEnabledChannels(userId, type);
  void channels;
  const row = await api.post<BackendNotification>('/api/v1/notifications', {
    userId,
    type,
    payload,
  });
  return shape(row);
}

// ─── Test helpers ──────────────────────────────────────────────────────

/**
 * SÓ tests — invalida o cache de todas as queries de notification.
 * Substitui o reset in-memory legado.
 */
export function __resetAllNotificationsForTests(): void {
  // Sem cache global aqui — invalidação via QueryClient é responsabilidade
  // do test setup. Mantido pra compat de imports legados (no-op seguro).
}
